import React, { useMemo, useState } from "react";
import { CheckCircle2, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { errMsg } from "../lib/api";
import { KIND_LABELS } from "../lib/batchLogs";

const fmt = (n) => (n || 0).toLocaleString("en-IN");

/**
 * Phase 151 — confirm removing batch HISTORY entries (soft-delete of the log record only).
 * Props:
 *   open, onOpenChange
 *   items     [{kind, id, filename?}]  — what is selected
 *   request   async (items) => result  — performs the delete (tenant or Super Admin endpoint)
 *   onDone    (result) => void         — called after a successful request
 *   testId    prefix for data-testid (default "delete-batch-logs")
 */
export const DeleteBatchLogsDialog = ({ open, onOpenChange, items = [], request, onDone, testId = "delete-batch-logs" }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const byKind = useMemo(() => {
    const m = {};
    items.forEach((it) => { m[it.kind] = (m[it.kind] || 0) + 1; });
    return Object.entries(m);
  }, [items]);

  const close = (v) => { if (busy) return; if (!v) setResult(null); onOpenChange(v); };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await request(items.map(({ kind, id }) => ({ kind, id })));
      setResult(res);
      if (res.deleted > 0) {
        toast.success(`${fmt(res.deleted)} batch record${res.deleted === 1 ? "" : "s"} removed from the history`,
          { description: res.skipped?.length ? `${res.skipped.length} skipped (still processing)` : "Imported data was not affected" });
      } else {
        toast.warning("Nothing was removed", { description: res.skipped?.[0]?.reason_text || "The selected batches could not be deleted right now." });
      }
      onDone?.(res);
      if (!res.skipped?.length) { setResult(null); onOpenChange(false); }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const n = items.length;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Trash2 className="h-5 w-5 text-destructive" /> Delete {fmt(n)} batch record{n === 1 ? "" : "s"} from history?
          </DialogTitle>
          <DialogDescription>
            Only the history entries are removed from the Batch History lists. Nothing the batches imported changes.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-breakdown`}>
              {byKind.map(([k, c]) => (
                <Badge key={k} variant="outline" className="text-xs font-normal" data-testid={`${testId}-kind-${k}`}>
                  {KIND_LABELS[k] || k} <span className="ml-1 font-semibold tabular-nums">{fmt(c)}</span>
                </Badge>
              ))}
            </div>
            <Alert className="border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5" data-testid={`${testId}-note`}>
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" />
              <AlertTitle className="text-sm">Your data will not be affected</AlertTitle>
              <AlertDescription className="text-xs">
                Properties, demands, payments and receipts created by these batches stay exactly as they are — this does
                <b> not</b> undo, roll back or reverse anything. The entries simply disappear from the history lists;
                the Super Admin can restore them later from <b>Batch Logs</b>.
              </AlertDescription>
            </Alert>
            <p className="text-[11px] text-muted-foreground">
              Once removed, the Undo button and the Excel downloads of these batches are no longer available on this page.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`${testId}-result`}>
            <p className="text-sm flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> {fmt(result.deleted)} removed from the history.
            </p>
            {result.skipped?.length > 0 && (
              <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1" data-testid={`${testId}-skipped`}>
                <p className="font-medium">{fmt(result.skipped.length)} skipped</p>
                <ul className="list-disc pl-4 space-y-0.5 max-h-40 overflow-auto">
                  {result.skipped.slice(0, 20).map((s) => (
                    <li key={`${s.kind}:${s.id}`}><span className="font-medium">{s.filename || s.id}</span> — {s.reason_text || s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => close(false)} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</Button>
              <Button variant="destructive" onClick={confirm} disabled={busy || n === 0} data-testid={`${testId}-confirm`}>
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                {busy ? "Removing…" : `Confirm — delete ${fmt(n)} record${n === 1 ? "" : "s"}`}
              </Button>
            </>
          ) : (
            <Button onClick={() => close(false)} data-testid={`${testId}-close`}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Compact toolbar shown above a batch table when the viewer may delete batch logs.
 *   count      number of selected rows
 *   onClear    () => void
 *   onDelete   () => void (opens the dialog)
 *   testId     prefix
 */
export const BatchLogSelectionBar = ({ count, onClear, onDelete, testId = "batch-logs", disabled = false }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={`${testId}-selection-bar`}>
    {count > 0 && (
      <>
        <span className="text-xs font-medium tabular-nums" data-testid={`${testId}-selected-count`}>{fmt(count)} selected</span>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClear} data-testid={`${testId}-clear-selection`}>Clear</Button>
      </>
    )}
    <Button variant="destructive" size="sm" className="h-7" onClick={onDelete} disabled={disabled || count === 0}
      title={count === 0 ? "Tick the batches you want to remove from the history" : "Remove the selected batch records from the history (data is not affected)"}
      data-testid={`${testId}-delete-selected`}>
      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Selected{count > 0 ? ` (${fmt(count)})` : ""}
    </Button>
  </div>
);

export default DeleteBatchLogsDialog;
